import React from "react";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-2xl md:text-3xl font-display font-black text-fut-primary mb-4">
      {title}
    </h2>
    <div className="space-y-4 text-fut-text-light/82 leading-relaxed">
      {children}
    </div>
  </section>
);

const NoticeBox = ({ children }) => (
  <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-fut-primary/18 via-white/[.045] to-white/[.025] p-6 md:p-8 shadow-2xl shadow-fut-primary/10 my-10">
    <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-fut-primary/20 blur-3xl" />
    <div className="relative z-10">
      {children}
    </div>
  </div>
);

const Policies = ({ translations = {} }) => {
  const isSpanish = translations?.header?.home === "Inicio";

  const t = isSpanish
    ? {
        eyebrow: "Políticas del sitio",
        title: "Política de Privacidad, Términos y Descripción de Servicios",
        subtitle:
          "Esta página resume las políticas operativas y legales principales de San Jose Obrero LLC para visitantes, prospectos y clientes.",
        updated: "Última actualización: 14 de mayo de 2026",
        legalNoteTitle: "Aviso operativo",
        legalNote:
          "Este documento está preparado para publicación en el sitio web y como base operativa de cumplimiento. No sustituye la revisión de un abogado autorizado. Debe actualizarse si cambian los servicios, proveedores, cookies, formularios, pagos, leyes aplicables o prácticas internas de San Jose Obrero LLC.",
        privacyTitle: "Política de Privacidad",
        privacy: [
          "San Jose Obrero LLC recopila, usa, divulga y protege información personal relacionada con formularios, páginas de contacto, calendarios, mensajes, correos electrónicos, llamadas, SMS, WhatsApp, CRM, pasarelas de pago y otros canales digitales o físicos utilizados por la empresa.",
          "La información puede incluir nombre, correo electrónico, teléfono, dirección, idioma preferido, servicio de interés, mensajes, documentos, información de negocio, información fiscal, información crediticia, reportes de crédito, identificación, autorizaciones, firmas, consentimientos, registros de citas y comunicaciones.",
          "Cuando un servicio requiere revisar crédito, financiamiento, impuestos, identidad, estructura empresarial o documentación financiera, podemos solicitar información sensible únicamente para el propósito autorizado por el cliente.",
          "Actualmente no vendemos ni compartimos información personal para publicidad conductual entre contextos.",
          "Aplicamos medidas administrativas, técnicas y físicas razonables para proteger información personal. Ningún sistema es 100% seguro.",
          "Dependiendo de su jurisdicción y la ley aplicable, usted puede tener derechos de acceso, corrección, eliminación, portabilidad, limitación de uso de información sensible y exclusión de marketing.",
        ],
        termsTitle: "Términos y Condiciones",
        terms: [
          "Al usar el sitio web, enviar formularios, programar citas, pagar servicios, comunicarse con nosotros o utilizar cualquier servicio de San Jose Obrero LLC, usted acepta estos Términos y Condiciones.",
          "Usted acepta usar el sitio y los servicios únicamente para fines legales. Está prohibido interferir con el funcionamiento del sitio, intentar acceso no autorizado, copiar contenido sin permiso, suplantar identidad, enviar información falsa o usar formularios para spam.",
          "Cada servicio específico puede requerir contrato, autorización, alcance de trabajo, consentimiento o acuerdo separado.",
          "No garantizamos aprobación de crédito, financiamiento, préstamos, tarjetas, cuentas comerciales, resultados fiscales, eliminación de información crediticia, aumento de puntaje, aprobación bancaria, aprobación gubernamental, aceptación de proveedores, tiempos de procesamiento de terceros ni resultados específicos.",
          "El cliente es responsable de proporcionar información verdadera, completa y actualizada; revisar documentos antes de firmar o enviar; responder solicitudes de información; proteger credenciales; y cumplir con sus obligaciones fiscales y legales.",
          "Política de no reembolso: salvo que la ley aplicable exija lo contrario, todos los pagos, depósitos, tarifas de inicio, tarifas por fase, suscripciones, pagos finales y cargos por servicios iniciados son no reembolsables. Cualquier excepción será considerada únicamente en casos extremos, deberá solicitarse por escrito, dependerá de la etapa del trabajo, el tipo de servicio, los costos ya incurridos, obligaciones legales aplicables y quedará sujeta a revisión y aprobación por escrito de San Jose Obrero LLC. Disputas bancarias, contracargos o pagos fallidos pueden pausar o cancelar servicios hasta resolver la situación.",
        ],
        creditNoticeTitle: "Aviso importante sobre crédito y perfil financiero",
        creditNotice:
          "San José Obrero LLC no garantiza aprobación de crédito, aumento específico de puntaje ni eliminación de información negativa exacta, actual y verificable. El servicio consiste en análisis, educación, preparación documental, disputas de información potencialmente inexacta, incompleta o no verificable, seguimiento del proceso y orientación estratégica. Los resultados dependen de la información contenida en el reporte, la respuesta de burós, acreedores, cobradores y la conducta financiera del cliente durante el proceso.",
        servicesTitle: "Descripción Clara de Servicios",
        services: [
          {
            title: "Constitución de Empresas",
            text:
              "Búsqueda básica de nombre, apoyo con artículos de organización o incorporación, EIN, acuerdo operativo o documentación interna básica, orientación inicial de estructura y próximos pasos administrativos.",
          },
          {
            title: "Perfil Empresarial Profesional",
            text:
              "Desarrollo de presencia empresarial mediante dirección comercial o virtual, dominio, correo corporativo, teléfono profesional, DUNS cuando aplique, consistencia de información empresarial y organización documental.",
          },
          {
            title: "Preparación para Financiamiento",
            text:
              "Revisión de estructura, documentación, perfil empresarial, ingresos, estados financieros, requisitos básicos y preparación para opciones financieras según el perfil del cliente.",
          },
          {
            title: "Educación y Soporte de Crédito Personal",
            text:
              "Educación financiera, revisión documental, identificación de posibles errores, orientación sobre disputas y preparación de cartas o procesos administrativos cuando aplique, sin prometer eliminación de información exacta, verificable y vigente.",
          },
          {
            title: "Servicios Fiscales y Cumplimiento Básico",
            text:
              "Orientación sobre organización fiscal, preparación o apoyo documental, cumplimiento básico con IRS o agencias estatales, cartas, enmiendas, formularios o registros cuando corresponda al servicio contratado.",
          },
          {
            title: "Consultoría y Orientación Empresarial",
            text:
              "Análisis de estructura, procesos internos, documentación, rutas de capital, organización operativa, planificación básica, estrategia de cumplimiento y recomendaciones administrativas.",
          },
        ],
        contactTitle: "Contacto de privacidad y asuntos legales",
        contact:
          "San Jose Obrero LLC · jose@sanjoseobrerollc.com · 15437 Anacapa Rd, Victorville, CA 92392 · https://sanjoseobrerollc.com",
      }
    : {
        eyebrow: "Website policies",
        title: "Privacy Policy, Terms & Conditions, and Clear Description of Services",
        subtitle:
          "This page summarizes the main operational and legal policies of San Jose Obrero LLC for visitors, prospects, and clients.",
        updated: "Last updated: May 14, 2026",
        legalNoteTitle: "Operational notice",
        legalNote:
          "This document is prepared for website publication and as an operational compliance baseline. It does not replace review by a licensed attorney. It should be updated if San Jose Obrero LLC changes its services, providers, cookies, forms, payments, applicable laws, or internal practices.",
        privacyTitle: "Privacy Policy",
        privacy: [
          "San Jose Obrero LLC collects, uses, discloses, and protects personal information related to forms, contact pages, appointment calendars, messages, emails, calls, SMS, WhatsApp, CRM, payment gateways, and other digital or physical channels used by the company.",
          "Information may include name, email, phone, address, preferred language, service of interest, messages, documents, business information, tax information, credit information, credit reports, identification, authorizations, signatures, consents, appointment records, and communications.",
          "When a service requires review of credit, financing, taxes, identity, business structure, or financial documentation, we may request sensitive information only for the purpose authorized by the client.",
          "We currently do not sell or share personal information for cross-context behavioral advertising.",
          "We apply reasonable administrative, technical, and physical measures to protect personal information. No system is 100% secure.",
          "Depending on your jurisdiction and applicable law, you may have privacy rights including access, correction, deletion, portability, limitation of sensitive information use, and marketing opt-out.",
        ],
        termsTitle: "Terms & Conditions",
        terms: [
          "By using the website, submitting forms, scheduling appointments, paying for services, communicating with us, or using any San Jose Obrero LLC service, you accept these Terms & Conditions.",
          "You agree to use the site and services only for lawful purposes. You may not interfere with site operation, attempt unauthorized access, copy content without permission, impersonate others, submit false information, or use forms for spam.",
          "Each specific service may require a separate contract, authorization, scope of work, consent, or agreement.",
          "We do not guarantee credit approval, financing, loans, cards, trade accounts, tax results, removal of credit information, score increases, bank approval, government approval, vendor acceptance, third-party processing times, or specific results.",
          "The client is responsible for providing true, complete, and updated information; reviewing documents before signing or submitting them; responding to information requests; protecting credentials; and complying with tax and legal obligations.",
          "No-refund policy: unless applicable law requires otherwise, all payments, deposits, setup fees, phase fees, subscriptions, final payments, and charges for services already started are non-refundable. Any exception will be considered only in extreme cases, must be requested in writing, depends on the stage of work, type of service, costs already incurred, applicable legal obligations, and is subject to written review and approval by San Jose Obrero LLC. Bank disputes, chargebacks, or failed payments may pause or cancel services until the situation is resolved.",
        ],
        creditNoticeTitle: "Important notice about credit and financial profile",
        creditNotice:
          "San Jose Obrero LLC does not guarantee credit approval, any specific score increase, or removal of accurate, current, and verifiable negative information. The service consists of analysis, education, document preparation, disputes of information that may be inaccurate, incomplete, or unverifiable, process follow-up, and strategic guidance. Results depend on the information contained in the report, the response of credit bureaus, creditors, collectors, and the client’s financial conduct during the process.",
        servicesTitle: "Clear Description of Services",
        services: [
          {
            title: "Company Formation",
            text:
              "Basic name search, support with articles of organization or incorporation, EIN, operating agreement or basic internal documentation, initial structure guidance, and next administrative steps.",
          },
          {
            title: "Professional Business Profile",
            text:
              "Development of a formal business presence through business or virtual address, domain, corporate email, professional phone, DUNS when applicable, business information consistency, and document organization.",
          },
          {
            title: "Financing Preparation",
            text:
              "Review of structure, documentation, business profile, income, financial statements, basic requirements, and preparation for financial options according to the client profile.",
          },
          {
            title: "Personal Credit Education and Support",
            text:
              "Financial education, document review, identification of possible errors, dispute guidance, preparation of letters or administrative processes when applicable, without promising removal of accurate, verifiable, and current information.",
          },
          {
            title: "Tax Services and Basic Compliance",
            text:
              "General guidance on tax organization, preparation or document support, basic compliance with the IRS or state agencies, letters, amendments, forms, or records when applicable to the contracted service.",
          },
          {
            title: "Business Consulting and Guidance",
            text:
              "Analysis of structure, internal processes, documentation, capital routes, operational organization, basic planning, compliance strategy, and administrative recommendations.",
          },
        ],
        contactTitle: "Privacy and legal contact",
        contact:
          "San Jose Obrero LLC · jose@sanjoseobrerollc.com · 15437 Anacapa Rd, Victorville, CA 92392 · https://sanjoseobrerollc.com",
      };

  return (
    <main className="min-h-screen bg-[#050B14] text-fut-text-light font-sans overflow-hidden">
      <section className="relative px-6 pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_16%_14%,rgba(0,212,255,.22),transparent_26%),radial-gradient(circle_at_88%_24%,rgba(59,130,246,.16),transparent_28%),linear-gradient(135deg,#001A33_0%,#06111f_45%,#020617_100%)]" />
        <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-fut-primary/10 blur-3xl" />

        <div className="relative container mx-auto max-w-screen-xl">
          <div className="max-w-5xl mb-12">
            <p className="text-fut-primary font-bold uppercase tracking-[.22em] mb-5">
              {t.eyebrow}
            </p>
            <h1 className="text-4xl md:text-6xl font-display font-black leading-tight mb-5 text-white">
              {t.title}
            </h1>
            <p className="text-lg md:text-xl text-fut-text-light/85 leading-relaxed mb-4">
              {t.subtitle}
            </p>
            <p className="text-fut-primary font-semibold">{t.updated}</p>
          </div>

          <NoticeBox>
            <h2 className="text-2xl md:text-3xl font-display font-black text-white mb-3">
              {t.legalNoteTitle}
            </h2>
            <p className="text-fut-text-light/85 leading-relaxed">
              {t.legalNote}
            </p>
          </NoticeBox>

          <div className="relative rounded-[3rem] bg-white/[.045] p-6 md:p-10 shadow-2xl shadow-fut-primary/10">
            <Section title={t.privacyTitle}>
              {t.privacy.map((item) => (
                <p key={item}>{item}</p>
              ))}
            </Section>

            <Section title={t.termsTitle}>
              {t.terms.map((item) => (
                <p key={item}>{item}</p>
              ))}
            </Section>

            <NoticeBox>
              <p className="text-fut-primary font-black uppercase tracking-[.18em] text-sm mb-3">
                {t.creditNoticeTitle}
              </p>
              <p className="text-white text-lg leading-relaxed font-semibold">
                {t.creditNotice}
              </p>
            </NoticeBox>

            <Section title={t.servicesTitle}>
              <div className="grid md:grid-cols-2 gap-5">
                {t.services.map((service) => (
                  <article key={service.title} className="rounded-[2rem] bg-black/18 p-5">
                    <h3 className="text-fut-primary font-black text-xl mb-2">
                      {service.title}
                    </h3>
                    <p>{service.text}</p>
                  </article>
                ))}
              </div>
            </Section>

            <Section title={t.contactTitle}>
              <p>{t.contact}</p>
            </Section>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Policies;
